import { TAGS } from "@/store/api.ts"
import { createSocketBase } from "@/store/apis/socket.factory"
import { setEventFulfilled, setSessionId } from "@/store/slices/gameplay"
import { setMatchSession, setSocketConnected } from "@/store/slices/match"
import { deckEndpoints } from "../deckAPI"
import { effectsApi } from "../effectsAPI"
import { gameplayEndpoints } from "../gameplayAPI"
import { playerEndpoints } from "../playerAPI"
import { matchAPI, matchEndpoints } from "./match.api"
import {
  IMatchConfirmSessionResponse,
  IMatchRoomPlayersCount,
  IMatchRoomPlayersCountSocket,
  IMatchSessionResponse,
  MATCH_SOCKETS,
  MATCH_VARIANTS_SOCKETS
} from "./match.types"

export const socketMatch = {
  ...createSocketBase("matchmaking"),

  updateMatchSessionData: null as
    | ((updater: (draft: IMatchSessionResponse) => void) => void)
    | null,
  updateMatchConfirmSessionData: null as
    | ((updater: (draft: IMatchConfirmSessionResponse) => void) => void)
    | null,
  playerId: null as string | null,

  init(playerId: string) {
    this._init({
      auth: playerId ? { playerId } : undefined,
      onConnect: () => this.dispatch?.(setSocketConnected(true)),
      onConnectError: () => this.dispatch?.(setSocketConnected(false)),
      onDisconnect: () => this.dispatch?.(setSocketConnected(false)),
      onReconnect: () => this.dispatch?.(setSocketConnected(true))
    })
  },

  setPlayerId(playerId: string) {
    this.playerId = playerId
  },

  setUpdateMatchSessionData(
    updateCachedData: (updater: (draft: IMatchSessionResponse) => void) => void
  ) {
    this.updateMatchSessionData = updateCachedData
  },

  setUpdateMatchConfirmSessionData(
    updateCachedData: (
      updater: (draft: IMatchConfirmSessionResponse) => void
    ) => void
  ) {
    this.updateMatchConfirmSessionData = updateCachedData
  },

  bindMatchFoundListener() {
    const handler = (event: IMatchSessionResponse) => {
      console.log("[matchAPI]", MATCH_SOCKETS.MATCH_FOUND, event)

      this.dispatch?.(
        matchAPI.util.invalidateTags([{ type: TAGS.MATCH, id: `MY_SESSION` }])
      )
    }
    return this._bind(MATCH_SOCKETS.MATCH_FOUND, handler)
  },

  bindSessionReadyListener() {
    const handler = (event: IMatchSessionResponse) => {
      console.log("[matchAPI]", MATCH_SOCKETS.SESSION_READY, event)

      this.dispatch?.(
        playerEndpoints.util.updateQueryData(
          "getPlayerProfile",
          undefined,
          (draft) => {
            draft.inQueue = false
            draft.matchBidVariant = undefined
          }
        )
      )
      this.dispatch?.(
        matchAPI.util.invalidateTags([{ type: TAGS.MATCH, id: `MY_SESSION` }])
      )
      this.dispatch?.(
        gameplayEndpoints.util.invalidateTags([
          { type: TAGS.GAMEPLAY, id: `STATE` }
        ])
      )
      this.dispatch?.(deckEndpoints.util.invalidateTags([{ type: TAGS.DECK }]))
      this.dispatch?.(effectsApi.util.invalidateTags([{ type: TAGS.EFFECTS }]))
    }
    return this._bind(MATCH_SOCKETS.SESSION_READY, handler)
  },

  bindSessionFinishedListener() {
    const handler = (event: IMatchSessionResponse) => {
      console.log("[matchAPI]", MATCH_SOCKETS.MATCH_FINISHED, event)

      this.dispatch?.(
        matchEndpoints.util.upsertQueryData(
          "getMyMatchSession",
          {
            playerId: this.playerId || ""
          },
          event
        )
      )
      this.dispatch?.(setMatchSession(event))
      this.dispatch?.(setSessionId(event.sessionId))

      this.updateMatchSessionData?.(() => {
        return event
      })

      this.dispatch?.(setEventFulfilled(true))
    }

    return this._bind(MATCH_SOCKETS.MATCH_FINISHED, handler)
  },

  bindSessionConfirmListener() {
    const handler = (event: IMatchConfirmSessionResponse) => {
      console.log("[matchAPI]", MATCH_SOCKETS.SESSION_CONFIRM, event)

      if (event?.reason && ["EXPIRED", "REJECTED"].includes(event?.reason)) {
        this.dispatch?.(
          playerEndpoints.util.updateQueryData(
            "getPlayerProfile",
            undefined,
            (draft) => {
              draft.inQueue = false
              draft.matchBidVariant = undefined
            }
          )
        )
        this.dispatch?.(
          matchAPI.util.invalidateTags([
            { type: TAGS.MATCH, id: `MATCH_SESSION` }
          ])
        )
        this.dispatch?.(
          matchAPI.util.invalidateTags([{ type: TAGS.MATCH, id: `MY_SESSION` }])
        )
      }

      this.updateMatchConfirmSessionData?.(() => {
        return event
      })
    }

    return this._bind(MATCH_SOCKETS.SESSION_CONFIRM, handler)
  }
}

export const socketMatchVariants = {
  ...createSocketBase("match_variants"),

  updateRoomPlayersCountData: null as
    | ((updater: (draft: IMatchRoomPlayersCount) => void) => void)
    | null,

  init() {
    this._init({
      onConnect: () =>
        this.dispatch?.(
          matchEndpoints.util.invalidateTags([
            { type: TAGS.MATCH, id: "ROOM_PLAYERS_COUNT" }
          ])
        )
    })
  },

  setUpdateRoomPlayersCountData(
    updateCachedData: (updater: (draft: IMatchRoomPlayersCount) => void) => void
  ) {
    this.updateRoomPlayersCountData = updateCachedData
  },

  bindQueueUpdatedListener() {
    const handler = (event: IMatchRoomPlayersCountSocket) => {
      console.log("[matchAPI]", MATCH_VARIANTS_SOCKETS.Q_UPDATED, event)
      this.updateRoomPlayersCountData?.(() => {
        return { counts: event.data }
      })
    }
    return this._bind(MATCH_VARIANTS_SOCKETS.Q_UPDATED, handler)
  }
}
